import { useState, useCallback } from 'react';
import * as adminApi from '../../api/wallet/admin.api';
import toast from 'react-hot-toast';

/**
 * Hook that wraps all admin API calls.
 * Uses plain Axios + local loading/error state.
 */
export const useAdmin = () => {
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState(null);

    const exec = useCallback(async (fn, ...args) => {
        setLoading(true);
        setError(null);
        try {
            const result = await fn(...args);
            return result;
        } catch (err) {
            setError(err);
            throw err;
        } finally {
            setLoading(false);
        }
    }, []);

    // Dashboard
    const fetchDashboard = useCallback(() => exec(adminApi.getDashboardStats), [exec]);

    // API Keys
    const fetchApiKeys = useCallback((p) => exec(adminApi.getApiKeys, p), [exec]);
    const doCreateApiKey = useCallback(
        async (data) => {
            const res = await exec(adminApi.createApiKey, data);
            toast.success('API key created!');
            return res;
        },
        [exec]
    );
    const doToggleApiKey = useCallback(
        async (id, data) => {
            const res = await exec(adminApi.toggleApiKey, id, data);
            toast.success(data.is_active ? 'API key enabled' : 'API key disabled');
            return res;
        },
        [exec]
    );

    // Users
    const fetchUsers = useCallback((p) => exec(adminApi.getUsers, p), [exec]);
    const fetchUserWallets = useCallback((id) => exec(adminApi.getUserWallets, id), [exec]);
    const fetchUserTransactions = useCallback(
        (id, p) => exec(adminApi.getUserTransactions, id, p),
        [exec]
    );

    // Wallet Transactions
    const fetchWalletTransactions = useCallback(
        (wId, p) => exec(adminApi.getWalletTransactions, wId, p),
        [exec]
    );

    // Limits
    const fetchLimits = useCallback(() => exec(adminApi.getLimits), [exec]);
    const doSetLimits = useCallback(
        async (data) => {
            const res = await exec(adminApi.setLimits, data);
            toast.success('Limits updated!');
            return res;
        },
        [exec]
    );

    // Reward Credit
    const doCreditReward = useCallback(
        async (data) => {
            const res = await exec(adminApi.creditReward, data);
            toast.success('Reward credited!');
            return res;
        },
        [exec]
    );

    // Closeout
    const doCloseout = useCallback(
        async (data) => {
            const res = await exec(adminApi.closeoutWallet, data);
            toast.success('Wallet closed out!');
            return res;
        },
        [exec]
    );

    // Payouts
    const fetchPayouts = useCallback((p) => exec(adminApi.getPayouts, p), [exec]);
    const doApprovePayout = useCallback(
        async (id) => {
            const res = await exec(adminApi.approvePayout, id);
            toast.success('Payout approved!');
            return res;
        },
        [exec]
    );
    const doProcessPayout = useCallback(
        async (id, data) => {
            const res = await exec(adminApi.processPayout, id, data);
            toast.success('Payout processed!');
            return res;
        },
        [exec]
    );

    // Settlements
    const doGenerateSettlement = useCallback(
        async (data) => {
            const res = await exec(adminApi.generateSettlement, data);
            toast.success('Settlement generated!');
            return res;
        },
        [exec]
    );
    const fetchSettlements = useCallback((p) => exec(adminApi.getSettlements, p), [exec]);
    const doConfirmSettlement = useCallback(
        async (id) => {
            const res = await exec(adminApi.confirmSettlement, id);
            toast.success('Settlement confirmed!');
            return res;
        },
        [exec]
    );

    // Revenue
    const fetchRevenueReport = useCallback(
        (p) => exec(adminApi.getRevenueReport, p),
        [exec]
    );

    // Holds
    const fetchHolds = useCallback((p) => exec(adminApi.getHolds, p), [exec]);

    const doCreateHold = useCallback(
        async (data) => {
            const res = await exec(adminApi.createHold, data);
            toast.success('Hold created!');
            return res;
        },
        [exec]
    );
    const doCaptureHold = useCallback(
        async (data) => {
            const res = await exec(adminApi.captureHold, data);
            toast.success('Hold captured!');
            return res;
        },
        [exec]
    );
    const doReleaseHold = useCallback(
        async (data) => {
            const res = await exec(adminApi.releaseHold, data);
            toast.success('Hold released!');
            return res;
        },
        [exec]
    );
    const fetchHold = useCallback((id) => exec(adminApi.getHold, id), [exec]);

    return {
        loading,
        error,
        fetchDashboard,
        fetchApiKeys,
        doCreateApiKey,
        doToggleApiKey,
        fetchUsers,
        fetchUserWallets,
        fetchUserTransactions,
        fetchWalletTransactions,
        fetchLimits,
        doSetLimits,
        doCreditReward,
        doCloseout,
        fetchPayouts,
        doApprovePayout,
        doProcessPayout,
        doGenerateSettlement,
        fetchSettlements,
        doConfirmSettlement,
        fetchRevenueReport,
        fetchHolds,
        doCreateHold,
        doCaptureHold,
        doReleaseHold,
        fetchHold,
    };
};
